import React, { useEffect, useMemo, useRef } from "react";

type MeterProps = {
    oldValue?: number;
    newValue?: number;
    animationDuration?: number; // milliseconds
    dividers?: number[]; // Example: [20, 80]
    finished?: boolean;
};

export const Meter = ({
    oldValue = 0,
    newValue = 0,
    animationDuration = 500,
    dividers = [],
    finished = false,
}: MeterProps) => {
    const needleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const clampedOld = Math.max(0, Math.min(100, oldValue));
        const clampedNew = Math.max(0, Math.min(100, newValue));

        const oldRotation = -270 + (clampedOld / 100) * 180;
        const newRotation = -270 + (clampedNew / 100) * 180;

        if (needleRef.current) {
            // Set initial position without transition
            needleRef.current.style.transition = "none";
            needleRef.current.style.transform = `rotate(${oldRotation}deg)`;

            // Force reflow to apply the initial position immediately
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            needleRef.current.offsetHeight;

            // Animate to new position with CSS transition (GPU-accelerated)
            const timeoutId = window.setTimeout(() => {
                if (needleRef.current) {
                    needleRef.current.style.transition = `transform ${animationDuration}ms cubic-bezier(0.215, 0.61, 0.355, 1)`;
                    needleRef.current.style.transform = `rotate(${newRotation}deg)`;
                }
            }, 50);

            return () => window.clearTimeout(timeoutId);
        }
    }, [oldValue, newValue, animationDuration]);

    const dividerRotations = useMemo(() => {
        const clamp = (v: number) => Math.max(0, Math.min(100, v));
        const unique = Array.from(new Set(dividers.map(clamp)));
        return unique.map((pct) => ({
            pct,
            deg: -270 + (pct / 100) * 180,
        }));
    }, [dividers]);

    return (
        <div className="meterRoot">
            <div className="meterFrame">
                <div className="meterBackground" />
                <div className="meterGlare" />

                <div
                    className={`meterReward reward-1 ${finished ? "finished" : ""} ${dividers && dividers[0] !== undefined && newValue < dividers[0] ? "highlighted" : ""}`}
                />
                <div
                    className={`meterReward reward-2 ${finished ? "finished" : ""} ${dividers && dividers[0] !== undefined && dividers[1] !== undefined && newValue >= dividers[0] && newValue < dividers[1] ? "highlighted" : ""}`}
                />
                <div
                    className={`meterReward reward-3 ${finished ? "finished" : ""} ${dividers && dividers[1] !== undefined && newValue >= dividers[1] ? "highlighted" : ""}`}
                />

                {dividerRotations.map(({ pct, deg }) => (
                    <div
                        key={pct}
                        className="dividerPivot"
                        style={{ transform: `rotate(${deg}deg)` }}
                        aria-hidden="true"
                    >
                        <div className="dividerNeedle" />
                    </div>
                ))}

                {dividerRotations.map(({ pct, deg }) => (
                    <div
                        key={pct}
                        className="dividerPivot"
                        style={{ transform: `rotate(${deg}deg)` }}
                        aria-hidden="true"
                    >
                        <div className="dividerNeedle" />
                    </div>
                ))}

                <div
                    ref={needleRef}
                    className="needlePivot"
                    style={{ willChange: "transform" }}
                >
                    <div className="needle" />
                </div>

                <div className="centerPin" />
            </div>
        </div>
    );
};
